import { useEffect, useRef } from "react";

/* ══════════════════════════════════════════════════════════════════════════
   InstaPass orb — v2.

   The old orb was a <canvas> particle field. This one is the treatment from
   the Netflix concept build: no canvas at all, no per-frame JS. Everything
   is CSS —

     the disc      radial-gradient core, five stacked box-shadows for the rim
                   and the two bloom halos
     the sweep     a conic-gradient rotating once every 16s, set to
                   mix-blend-mode:screen so it reads as light travelling under
                   glass rather than a wedge painted on top
     the rings     four of them, below. This is the part that makes it feel
                   alive rather than merely lit.
     the mark      the 3D INSTAPASS logo, as an animated WebP

   ── The rings ───────────────────────────────────────────────────────────
   Three pulse rings share one 4.8s keyframe and are staggered by a third of
   it each, so there is always a ring somewhere mid-flight and the eye never
   catches a gap. They scale from the rim outward and fade as they go — a
   sonar ping, which is the right metaphor: she is listening.

   The fourth is different. It doesn't pulse; it orbits — a dashed ring that
   rotates continuously, giving the orb a sense of machinery turning even
   while everything else is still. Counter-rotating against the conic sweep
   keeps the two from ever looking like one locked object.

   ── The mark ────────────────────────────────────────────────────────────
   An animated WebP, not a video: the source render is a rotation on a black
   field, and the black had to go. Keying it to real alpha and encoding to
   WebP gives transparency that works in every browser, in one file, with no
   codec branching and no blend-mode trick that would wash out the white
   PASS letters against the red glow. It's a ping-pong loop (forward then
   reversed) so the sweep never snaps back to its start angle.

   Note the repo's older instapass-3d-logo-transparent.png is NOT used here:
   its matte is broken — there are horizontal slashes cut through the INSTA
   letters where the key failed. The WebP is a fresh cut from the render.

   ── The amplitude wire ──────────────────────────────────────────────────
   `--amp` (0..1) drives rim brightness, ring intensity and scale. While
   Hailey speaks the chat writes real waveform RMS into it frame by frame,
   so the orb pulses on her actual voice. With no waveform the CSS falls back
   to a small fast tremble, so she still reads as talking.

   States are unchanged from v1 — 'idle' | 'listening' | 'thinking' |
   'speaking' — so nothing that drives the orb has to change.
   ══════════════════════════════════════════════════════════════════════════ */

export function InstaPassOrb({
  state = "idle",
  amp = 0,
  onTap,
  /* Render as a <span> instead of a <button>. Needed when the orb is used as
     the face of a control that is already a button — the floating launcher —
     because a button inside a button is invalid HTML and browsers respond by
     silently unnesting it, which breaks the click target. */
  interactive = true,
  className = "",
}) {
  const elRef = useRef(null);

  /* Amplitude goes straight to the style attribute rather than through React
     state: this updates on every animation frame while she speaks, and a
     re-render per frame would be waste for what is one CSS variable. */
  useEffect(() => {
    elRef.current?.style.setProperty("--amp", String(Math.max(0, Math.min(1, amp))));
  }, [amp]);

  const Tag = interactive ? "button" : "span";

  return (
    <Tag
      ref={elRef}
      {...(interactive
        ? { type: "button", onClick: onTap, "aria-label": "Tap to speak with InstaPass AI" }
        : { "aria-hidden": "true" })}
      className={`ip-orb ${state}${state === "speaking" && amp > 0 ? " live" : ""} ${className}`}
    >
      <span className="ip-orb-ring" aria-hidden="true" />
      <span className="ip-orb-ring" aria-hidden="true" />
      <span className="ip-orb-ring" aria-hidden="true" />
      <span className="ip-orb-orbit" aria-hidden="true" />
      <span className="ip-orb-sweep" aria-hidden="true" />
      <img
        className="ip-orb-mark"
        src="/assets/instapass-mark-loop.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
      />
    </Tag>
  );
}

export default InstaPassOrb;
